import React, { useEffect, useState, useCallback } from 'react';
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { Users, TrendingUp, Activity, X, ChevronRight, Search, ArrowDown, ArrowUp, Eye, EyeOff, Ban, ShieldCheck, Sparkles, Info } from 'lucide-react';
import { useColumnVisibility, ColumnDef } from '../hooks/useColumnVisibility';
import ColumnVisibilityMenu from '../components/ColumnVisibilityMenu';

/** Per-user accepted-vs-corrected tally for one AI-signal grouping (feature or model). */
interface AiSignalGroup {
    total: number;
    accepted: number;
    corrected: number;
}

/**
 * Per-user aggregate over the `ai_correction_signals` table, computed server-side
 * by `GET /api/v1/hq/users`. "accepted" = the user kept the AI suggestion;
 * "corrected" = the user changed it.
 */
interface AiSignalStats {
    total: number;
    accepted: number;
    corrected: number;
    /** Keyed by `feature`: categorization | duplicate | merchant | auto_save. */
    byFeature: Record<string, AiSignalGroup>;
    /** Keyed by `model`: rule-based | gemma-4-e2b. */
    byModel: Record<string, AiSignalGroup>;
    /**
     * Tier A merchant-normalization outcomes (feature='merchant', model='rule-based'):
     * how often the user had to fix the rule-based name. Raw input to `onDeviceLlmFit`.
     */
    merchantRuleBased: { total: number; corrected: number };
}

/** Fit tier from the server's `computeOnDeviceLlmFit`, low → high. */
type OnDeviceLlmFitTier = 'ineligible' | 'low' | 'moderate' | 'strong';

/**
 * "Would allowlisting this user for Gemma Tier B actually help?" — computed
 * server-side (`utils/onDeviceLlmFit.js`) from data `GET /hq/users` already
 * fetches. Transparent: `tier` is backed by `blockers`, `demand`, `engagement`.
 */
interface OnDeviceLlmFit {
    tier: OnDeviceLlmFitTier;
    /** 0–100 sort key used by the candidate shortlist. */
    score: number;
    /** 'device_not_capable' | 'app_outdated' | 'app_version_unknown' | 'dormant' */
    blockers: string[];
    demand: {
        ruleBasedMerchantSignals: number;
        ruleBasedCorrectionRate: number | null;
        smsTransactions: number;
    };
    engagement: { txCount: number; activeLast30d: boolean; dormant: boolean };
}

const EMPTY_LLM_FIT: OnDeviceLlmFit = {
    tier: 'ineligible',
    score: 0,
    blockers: [],
    demand: { ruleBasedMerchantSignals: 0, ruleBasedCorrectionRate: null, smsTransactions: 0 },
    engagement: { txCount: 0, activeLast30d: false, dormant: false },
};

/** Chip colours per fit tier. */
const LLM_FIT_TIER_STYLES: Record<OnDeviceLlmFitTier, string> = {
    strong: 'bg-emerald-400/10 border-emerald-400/30 text-emerald-400',
    moderate: 'bg-yellow-400/10 border-yellow-400/30 text-yellow-400',
    low: 'bg-zinc-800 border-zinc-700 text-zinc-400',
    ineligible: 'bg-zinc-800 border-zinc-700 text-zinc-600',
};

/** Human-readable label for each `onDeviceLlmFit.blockers` code. */
const LLM_FIT_BLOCKER_LABELS: Record<string, string> = {
    device_not_capable: 'Device below capability floor',
    app_outdated: 'App below 7.0.0 (no LiteRT-LM module)',
    app_version_unknown: 'App version never reported',
    dormant: 'Dormant account',
};

/** The single deciding gate behind a user's `onDeviceLLM` flag. */
type OnDeviceLlmReason =
    | 'kill_switch'
    | 'ai_features_disabled'
    | 'device_not_capable'
    | 'allowlisted'
    | 'rollout_disabled'
    | 'rollout_full'
    | 'in_rollout'
    | 'not_in_rollout';

/** The single deciding gate behind a user's `aiChat` flag — one level above `onDeviceLLM`. */
type AiChatReason =
    | 'kill_switch'
    | 'on_device_llm_disabled'
    | 'allowlisted'
    | 'rollout_disabled'
    | 'rollout_full'
    | 'in_rollout'
    | 'not_in_rollout';

/**
 * On-device AI / LLM rollout status for one user — mirrors exactly what
 * `GET /users/me` hands that user's app, plus the "why".
 */
interface AiStatus {
    /** `features.onDeviceAI` as the app sees it: ai_features_enabled AND device_ai_capable. */
    onDeviceAI: boolean;
    /** `features.onDeviceLLM` as the app sees it (kill switch → prereqs → allowlist → rollout bucket). */
    onDeviceLLM: boolean;
    /** Per-user AI opt-in (`users.ai_features_enabled`), toggled from this page. */
    aiFeaturesEnabled: boolean;
    /** RAM/API-level capability floor (`users.device_ai_capable`). */
    deviceAiCapable: boolean;
    /** `users.metadata.on_device_llm_allowlisted === true` — force-enables the LLM tier. */
    onDeviceLlmAllowlisted: boolean;
    /** Which gate decided `onDeviceLLM`. */
    onDeviceLlmReason: OnDeviceLlmReason;
    /** Stable 0–99 bucket from the firebase_uid hash; null if the user has no firebase_uid. */
    onDeviceLlmBucket: number | null;
    /** Current `ON_DEVICE_LLM_ROLLOUT_PERCENT`; a user is in when bucket < percent. */
    onDeviceLlmRolloutPercent: number;
    /** "Ask Hisab" chat flag as the app sees it — rides on top of `onDeviceLLM`, shares its rollout bucket. */
    aiChat: boolean;
    /** `users.metadata.ai_chat_allowlisted === true` — force-enables chat (still needs `onDeviceLLM`). */
    aiChatAllowlisted: boolean;
    /** Which gate decided `aiChat`. */
    aiChatReason: AiChatReason;
    /** Current `AI_CHAT_ROLLOUT_PERCENT`; a user is in when the shared bucket < percent. */
    aiChatRolloutPercent: number;
}

/** Zeroed AI-signal aggregate — the fallback when a row arrives without one. */
const EMPTY_AI_SIGNALS: AiSignalStats = {
    total: 0,
    accepted: 0,
    corrected: 0,
    byFeature: {},
    byModel: {},
    merchantRuleBased: { total: 0, corrected: 0 },
};

/**
 * Backfills `aiStatus` / `aiSignals` on a user row so the page still renders
 * against a backend that predates those fields (deploy-order safety). A stale
 * backend simply shows every AI flag as off / no signals until it ships.
 */
function normalizeUser(u: UserInsight): UserInsight {
    return {
        ...u,
        aiStatus: u.aiStatus ?? {
            onDeviceAI: false,
            onDeviceLLM: false,
            aiFeaturesEnabled: !!u.ai_features_enabled,
            deviceAiCapable: !!u.device_ai_capable,
            onDeviceLlmAllowlisted: false,
            onDeviceLlmReason: 'ai_features_disabled',
            onDeviceLlmBucket: null,
            onDeviceLlmRolloutPercent: 0,
            aiChat: false,
            aiChatAllowlisted: false,
            aiChatReason: 'on_device_llm_disabled',
            aiChatRolloutPercent: 0,
        },
        aiSignals: u.aiSignals
            ? { ...EMPTY_AI_SIGNALS, ...u.aiSignals }
            : EMPTY_AI_SIGNALS,
        onDeviceLlmFit: u.onDeviceLlmFit ?? EMPTY_LLM_FIT,
    };
}

/** Human-readable label for each `onDeviceLLM` reason code. */
const LLM_REASON_LABELS: Record<OnDeviceLlmReason, string> = {
    kill_switch: 'Kill switch engaged',
    ai_features_disabled: 'AI features off for this user',
    device_not_capable: 'Device below capability floor',
    allowlisted: 'Allowlisted (metadata)',
    rollout_disabled: 'Rollout at 0%',
    rollout_full: 'Rollout at 100%',
    in_rollout: 'In rollout bucket',
    not_in_rollout: 'Outside rollout bucket',
};

/** Human-readable label for each `aiChat` reason code. */
const AI_CHAT_REASON_LABELS: Record<AiChatReason, string> = {
    kill_switch: 'Kill switch engaged',
    on_device_llm_disabled: 'On-Device LLM disabled',
    allowlisted: 'Allowlisted (metadata)',
    rollout_disabled: 'Rollout at 0%',
    rollout_full: 'Rollout at 100%',
    in_rollout: 'In rollout bucket',
    not_in_rollout: 'Outside rollout bucket',
};

/**
 * A user record as returned by `GET /api/v1/hq/users`.
 * Includes per-user transaction aggregates computed server-side.
 */
interface UserInsight {
    /** Firebase UID — primary key in the Supabase `users` table. */
    id: string;
    /** User's email address. Null if not available in the profile. */
    email: string | null;
    /** User's display name set during sign-up. Null if not provided. */
    display_name: string | null;
    /** ISO 8601 timestamp of when the user row was created. */
    created_at: string;
    /** Whether on-device AI features are enabled for this user. */
    ai_features_enabled: boolean;
    /** Whether the account is blocked (e.g. bot/abuse) via the HQ dashboard. Blocked users get 403'd on every authenticated app route. */
    is_blocked: boolean;
    /** ISO 8601 timestamp of when the account was blocked. Null if never blocked / currently unblocked. */
    blocked_at: string | null;
    /** Optional admin-supplied reason for the block. */
    blocked_reason: string | null;
    /** Admin email that performed the most recent block/unblock action. */
    blocked_by: string | null;
    /** Last native app version reported by the mobile app. */
    last_seen_app_version: string | null;
    /** Last Expo OTA update id reported by the mobile app. */
    last_seen_ota_update_id: string | null;
    /** Last Expo runtime version reported by the mobile app. */
    last_seen_runtime_version: string | null;
    /** ISO 8601 timestamp of the reported OTA bundle creation date. */
    last_seen_ota_created_at: string | null;
    /** ISO 8601 timestamp of the last app-version telemetry heartbeat. */
    last_seen_at: string | null;
    /** Device capability: true if device supports on-device AI model execution. */
    device_ai_capable: boolean | null;
    /** Device RAM size in MB. */
    device_ram_mb: number | null;
    /** Android OS API level of the user's device. */
    device_api_level: number | null;
    /** Brand and model of the hardware device. */
    device_model: string | null;
    /** Timestamp when device specs were last reported. */
    device_last_checked: string | null;
    /** Opaque JSON metadata database column. */
    metadata: Record<string, any> | null;
    /** On-device AI / LLM rollout status, computed server-side. */
    aiStatus: AiStatus;
    /** Per-user accepted-vs-corrected AI-signal aggregate, computed server-side. */
    aiSignals: AiSignalStats;
    /** "Would Gemma Tier B help this user?" fit assessment, computed server-side. */
    onDeviceLlmFit: OnDeviceLlmFit;
    /** Total number of transactions recorded by this user. */
    txCount: number;
    /** Sum of all income transaction amounts in INR. */
    totalIncome: number;
    /** Sum of all expense transaction amounts in INR. */
    totalExpense: number;
    /**
     * ISO 8601 timestamp of the user's last telemetry heartbeat (mirrors
     * {@link UserInsight.last_seen_at}). Null if the app has not phoned home
     * since the heartbeat shipped. This is "last opened the app", not "last
     * recorded a transaction".
     */
    lastActive: string | null;
    /** Accumulated scan stats from the Dummy-SMS table. */
    smsStats: {
        total: number;
        pending: number;
        processed: number;
        ignored: number;
    };
}

/**
 * Paginated response shape returned by `GET /api/v1/hq/users`.
 * `growthData` is only populated on page 1 to avoid redundant queries.
 */
interface InsightsData {
    /** Users for the current page, each enriched with transaction aggregates. */
    users: UserInsight[];
    /** Total number of users across all pages. */
    total: number;
    /** Current 1-based page number. */
    page: number;
    /** Number of users per page. */
    limit: number;
    /** Total number of pages given `total` and `limit`. */
    totalPages: number;
    /**
     * Daily new-user signup counts for the last 30 days.
     * Only present on page 1; null on subsequent pages.
     */
    growthData: { date: string; newUsers: number }[] | null;
    /** Global env-driven on-device LLM rollout state, for the page-level banner. */
    rollout: {
        /** `ON_DEVICE_LLM_ENABLED=false` — force-off for everyone. */
        onDeviceLlmKillSwitch: boolean;
        /** `ON_DEVICE_LLM_ROLLOUT_PERCENT` (0–100). */
        onDeviceLlmRolloutPercent: number;
    } | null;
}

/**
 * Small on/off pill used in the Users table's AI column.
 *
 * @param label - Short tag shown inside the pill (e.g. "AI", "LLM").
 * @param on    - Whether the flag is enabled (drives colour).
 * @param title - Optional hover tooltip (e.g. the LLM reason label).
 */
function AiFlagPill({ label, on, title }: { label: string; on: boolean; title?: string }) {
    return (
        <span
            title={title}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide border ${
                on
                    ? 'bg-yellow-400/10 border-yellow-400/30 text-yellow-400'
                    : 'bg-zinc-800 border-zinc-700 text-zinc-500'
            }`}
        >
            {label} {on ? 'on' : 'off'}
        </span>
    );
}

/**
 * Formats a numeric amount as an Indian Rupee string with no decimal places.
 *
 * @example
 * formatAmount(12500) // → '₹12,500'
 *
 * @param amount - Raw numeric amount in INR.
 * @returns Formatted string prefixed with the ₹ symbol.
 */
function formatAmount(amount: number): string {
    return '₹' + amount.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

/**
 * Formats an ISO 8601 timestamp into a short human-readable date string.
 *
 * @example
 * formatDate('2024-10-24T14:21:00Z') // → 'Oct 24, 2024'
 *
 * @param dateStr - ISO 8601 date string.
 * @returns Formatted string like "Oct 24, 2024".
 */
function formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
}

/**
 * Formats an ISO 8601 timestamp as a human-friendly relative label.
 * Falls back to {@link formatDate} for dates older than 30 days.
 *
 * @example
 * formatRelativeDate(null)             // → '—'
 * formatRelativeDate(todayIso)         // → 'Today'
 * formatRelativeDate(yesterdayIso)     // → 'Yesterday'
 * formatRelativeDate(threeDaysAgoIso)  // → '3d ago'
 * formatRelativeDate(twoWeeksAgoIso)   // → '2w ago'
 * formatRelativeDate(oldIso)           // → 'Oct 24, 2024'
 *
 * @param dateStr - ISO 8601 date string, or null if unknown.
 * @returns A relative label string, or '—' for null input.
 */
function formatRelativeDate(dateStr: string | null): string {
    if (!dateStr) return '—';
    const diff = Date.now() - new Date(dateStr).getTime();
    const days = Math.floor(diff / 86400000);
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days}d ago`;
    if (days < 30) return `${Math.floor(days / 7)}w ago`;
    return formatDate(dateStr);
}

/**
 * Formats native app and OTA metadata without exposing opaque OTA UUIDs.
 *
 * @param user - UserInsight row with app-version telemetry fields.
 * @returns Label such as "v1.4.2 · OTA Apr 19", "v1.4.1 · embedded", or "—".
 */
function formatAppOtaLabel(user: Pick<UserInsight, 'last_seen_app_version' | 'last_seen_ota_update_id' | 'last_seen_ota_created_at'>): string {
    if (!user.last_seen_app_version && !user.last_seen_ota_update_id && !user.last_seen_ota_created_at) {
        return '—';
    }

    const appVersion = user.last_seen_app_version ? `v${user.last_seen_app_version}` : 'unknown version';

    if (user.last_seen_ota_created_at) {
        const otaDate = new Date(user.last_seen_ota_created_at).toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
        return `${appVersion} · OTA ${otaDate}`;
    }

    if (user.last_seen_ota_update_id === 'embedded') {
        return `${appVersion} · embedded`;
    }

    return `${appVersion} · OTA reported`;
}

/**
 * Derives up to two uppercase initials for a user avatar.
 * Priority: display_name words → first two chars of email → first two chars of user ID.
 *
 * @param user - UserInsight row with optional display_name and email.
 * @returns 1–2 uppercase characters suitable for an avatar placeholder.
 */
function getUserInitials(user: UserInsight): string {
    const name = user.display_name || user.email || '';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    if (parts[0]) return parts[0].slice(0, 2).toUpperCase();
    return user.id.slice(0, 2).toUpperCase();
}

/**
 * Returns the best available display name for a user row.
 * Falls back progressively: display_name → email → first 8 chars of user ID.
 *
 * @param user - UserInsight row with optional display_name and email.
 * @returns A human-readable string identifying the user.
 */
function getUserDisplayName(user: UserInsight): string {
    return user.display_name || user.email || user.id.slice(0, 8);
}

/**
 * A single transaction row as returned by `GET /api/v1/hq/users/:userId/transactions`.
 */
interface UserTransaction {
    /** UUID primary key of the transaction. */
    id: string;
    /** Transaction amount in INR. */
    amount: number;
    /** Whether the transaction is income or expense. Case may vary on legacy rows. */
    type: 'income' | 'expense' | 'INCOME' | 'EXPENSE';
    /** Logical date of the transaction (YYYY-MM-DD). */
    date: string;
    /** ISO 8601 timestamp of when the row was inserted. */
    created_at: string;
    /** Optional human-readable description entered by the user. */
    description: string | null;
    /** Origin of the transaction: 'manual' or 'sms'. */
    source: string | null;
}

/**
 * Paginated response shape returned by `GET /api/v1/hq/users/:userId/transactions`.
 */
interface UserTransactionsData {
    transactions: UserTransaction[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
}

interface SmsLog {
    id: string;
    rawSMS: string;
    sender: string;
    status: 'pending' | 'processed' | 'ignored';
    isTransaction: boolean;
    sms_date: string | null;
    created_at: string;
    transactionalSmsDump: string | null;
}

interface UserSmsData {
    smsLogs: SmsLog[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
}

/**
 * Formats an ISO 8601 timestamp into a short human-readable date-time string
 * for use inside the transactions drawer.
 *
 * @example
 * formatDateTime('2024-10-24T14:21:00Z') // → 'Oct 24, 2024 · 14:21'
 *
 * @param dateStr - ISO 8601 date string (e.g. from `created_at`).
 * @returns Formatted string like "Oct 24, 2024 · 14:21".
 */
function formatDateTime(dateStr: string): string {
    const d = new Date(dateStr);
    return (
        d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }) +
        ' · ' +
        d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })
    );
}

/** Number of user rows fetched per page from the API. */
const PAGE_LIMIT = 20;

/** localStorage key the Users table's column preference is persisted under. */
const COLUMNS_STORAGE_KEY = 'hq-insights-users-columns';

/**
 * Columns rendered in the Users table, in display order. `key` values are
 * persisted to localStorage as part of {@link COLUMNS_STORAGE_KEY} — never
 * rename one without a migration. `user` is required (it's the row's click
 * target and only identifier), everything else defaults to visible but can
 * be hidden from the "Columns" menu.
 */
const USER_TABLE_COLUMNS: ColumnDef[] = [
    { key: 'user', label: 'User', required: true },
    { key: 'joined', label: 'Joined' },
    { key: 'transactions', label: 'Transactions' },
    { key: 'sms', label: 'SMS Logs' },
    { key: 'income', label: 'Income' },
    { key: 'expense', label: 'Expense' },
    { key: 'ai', label: 'AI' },
    { key: 'appOta', label: 'App / OTA' },
    { key: 'lastSeen', label: 'Last Seen' },
];

/** Number of transaction rows fetched per page inside the user drawer. */
const DRAWER_PAGE_LIMIT = 15;

/** One row from `GET /api/v1/hq/on-device-llm/candidates`. */
interface LlmCandidate {
    id: string;
    email: string | null;
    display_name: string | null;
    device_model: string | null;
    last_seen_app_version: string | null;
    last_seen_at: string | null;
    aiStatus: AiStatus;
    onDeviceLlmFit: OnDeviceLlmFit;
    merchantRuleBased: { total: number; corrected: number };
}

type CandidateMinTier = 'low' | 'moderate' | 'strong';

/**
 * Ranked "allowlist these next" shortlist for the Gemma Tier B rollout, backed by
 * `GET /hq/on-device-llm/candidates`. Select rows (or all) and push them through
 * `POST /hq/on-device-llm/allowlist-bulk` in one go. Calls `onChanged` after a
 * successful write so the parent Users table re-fetches.
 */
function TierBCandidatesModal({
    getToken,
    onClose,
    onChanged,
}: {
    getToken: () => Promise<string | null>;
    onClose: () => void;
    onChanged: () => void;
}) {
    const [minTier, setMinTier] = useState<CandidateMinTier>('moderate');
    const [rows, setRows] = useState<LlmCandidate[] | null>(null);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [submitting, setSubmitting] = useState(false);

    const fetchCandidates = useCallback(async (tier: CandidateMinTier) => {
        setLoading(true);
        setError('');
        setSelected(new Set());
        try {
            const token = await getToken();
            if (!token) return;
            const res = await fetch(
                `/api/v1/hq/on-device-llm/candidates?minTier=${tier}&excludeEnabled=true&limit=100`,
                { headers: { Authorization: `Bearer ${token}` } },
            );
            const json = await res.json();
            if (!res.ok || !json.success) throw new Error(json.error || 'Failed to fetch candidates');
            setRows(json.data.candidates || []);
            setTotal(json.data.total ?? (json.data.candidates || []).length);
        } catch (err: any) {
            console.error('TierBCandidatesModal fetch error:', err);
            setError(err.message || 'Failed to fetch candidates');
        } finally {
            setLoading(false);
        }
    }, [getToken]);

    useEffect(() => { fetchCandidates(minTier); }, [fetchCandidates, minTier]);

    const toggleRow = (id: string) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const allSelected = !!rows && rows.length > 0 && rows.every(r => selected.has(r.id));
    const toggleAll = () => {
        setSelected(allSelected ? new Set() : new Set((rows || []).map(r => r.id)));
    };

    const handleAllowlist = async () => {
        if (selected.size === 0) return;
        if (!window.confirm(`Allowlist ${selected.size} user(s) for Gemma Tier B?`)) return;
        setSubmitting(true);
        try {
            const token = await getToken();
            if (!token) return;
            const res = await fetch('/api/v1/hq/on-device-llm/allowlist-bulk', {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ userIds: [...selected], allowlisted: true }),
            });
            const json = await res.json();
            if (!res.ok || !json.success) throw new Error(json.error || 'Bulk allowlist failed');
            const failed = json.data?.failed?.length ?? 0;
            alert(`Allowlisted ${json.data?.updated ?? 0} user(s)` + (failed ? `, ${failed} failed` : ''));
            onChanged();
            await fetchCandidates(minTier);
        } catch (err: any) {
            console.error('TierBCandidatesModal bulk error:', err);
            alert(err.message || 'Bulk allowlist failed');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <div className="fixed inset-0 bg-zinc-950/70 backdrop-blur-sm z-[60]" onClick={onClose} />
            <div className="fixed inset-0 z-[70] flex items-center justify-center p-6 pointer-events-none">
                <div className="pointer-events-auto w-full max-w-3xl max-h-[85vh] bg-zinc-900 border border-zinc-800 rounded-2xl shadow-2xl flex flex-col">
                    <div className="flex items-center justify-between p-5 border-b border-zinc-800">
                        <div>
                            <div className="text-base font-bold text-zinc-100 flex items-center gap-2">
                                <Sparkles className="w-4 h-4 text-yellow-400" /> Gemma Tier B candidates
                            </div>
                            <div className="text-xs text-zinc-500 mt-0.5">
                                Ranked by fit score · not yet enabled · {total} match{total === 1 ? '' : 'es'}
                            </div>
                        </div>
                        <button onClick={onClose} className="text-zinc-500 hover:text-zinc-100 transition-colors">
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    <div className="flex items-center gap-3 px-5 py-3 border-b border-zinc-800">
                        <span className="text-xs text-zinc-500">Min tier</span>
                        <select
                            value={minTier}
                            onChange={e => setMinTier(e.target.value as CandidateMinTier)}
                            className="bg-zinc-800/80 text-sm text-zinc-200 border border-zinc-700/50 rounded-lg px-2 py-1 outline-none focus:border-yellow-400"
                        >
                            <option value="strong">strong</option>
                            <option value="moderate">moderate+</option>
                            <option value="low">low+</option>
                        </select>
                        <div className="flex-1" />
                        <button
                            onClick={handleAllowlist}
                            disabled={selected.size === 0 || submitting}
                            className="px-3 py-1.5 rounded-lg text-xs font-bold bg-yellow-400 text-zinc-950 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            {submitting ? 'Allowlisting…' : `Allowlist ${selected.size || ''}`}
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto">
                        {error && <div className="m-4 bg-red-500/10 border border-red-500/50 text-red-400 p-3 rounded-xl text-sm">{error}</div>}
                        {loading ? (
                            <div className="p-10 text-center text-zinc-500 text-sm">Loading candidates…</div>
                        ) : !rows?.length ? (
                            <div className="p-10 text-center text-zinc-500 italic text-sm">No candidates at this tier</div>
                        ) : (
                            <table className="w-full text-left text-sm">
                                <thead className="sticky top-0 bg-zinc-900">
                                    <tr className="border-b border-zinc-800 text-[11px] uppercase tracking-wider text-zinc-500">
                                        <th className="px-4 py-2 w-8"><input type="checkbox" checked={allSelected} onChange={toggleAll} /></th>
                                        <th className="px-4 py-2">User</th>
                                        <th className="px-4 py-2">Fit</th>
                                        <th className="px-4 py-2 text-right">Merchant fixes</th>
                                        <th className="px-4 py-2 text-right">SMS txns</th>
                                        <th className="px-4 py-2">App</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-zinc-800">
                                    {rows.map(r => (
                                        <tr key={r.id} className="hover:bg-zinc-800/40">
                                            <td className="px-4 py-2"><input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleRow(r.id)} /></td>
                                            <td className="px-4 py-2 text-zinc-200">{r.display_name || r.email || r.id.slice(0, 8)}</td>
                                            <td className="px-4 py-2">
                                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide border ${LLM_FIT_TIER_STYLES[r.onDeviceLlmFit.tier]}`}>
                                                    {r.onDeviceLlmFit.tier} · {r.onDeviceLlmFit.score}
                                                </span>
                                                {r.onDeviceLlmFit.blockers.length > 0 && (
                                                    <span className="ml-1 text-[10px] text-rose-400/80" title={r.onDeviceLlmFit.blockers.map(b => LLM_FIT_BLOCKER_LABELS[b] || b).join(', ')}>⚠</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-2 text-right text-zinc-400">
                                                {r.merchantRuleBased.corrected}/{r.merchantRuleBased.total}
                                            </td>
                                            <td className="px-4 py-2 text-right text-zinc-400">{r.onDeviceLlmFit.demand.smsTransactions}</td>
                                            <td className="px-4 py-2 text-zinc-400">{r.last_seen_app_version ? `v${r.last_seen_app_version}` : '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                    <div className="px-5 py-3 border-t border-zinc-800 text-[11px] text-zinc-500">
                        Allowlisting sets <span className="font-mono">metadata.on_device_llm_allowlisted</span>. The user still needs AI opt-in and must accept the in-app model download.
                    </div>
                </div>
            </div>
        </>
    );
}

type UserEntityKind = 'accounts' | 'bills' | 'subscriptions' | 'splits';

const formatInr = (n: number | string | null | undefined) =>
    `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const formatDay = (d?: string | null) =>
    d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }) : '—';

/** Per-entity row rendering for the drawer's read-only list tabs. */
const USER_ENTITY_CONFIG: Record<UserEntityKind, {
    label: string;
    empty: string;
    title: (r: any) => string;
    right: (r: any) => string;
    detail: (r: any) => string[];
}> = {
    accounts: {
        label: 'Accounts',
        empty: 'No accounts found',
        title: (r) => r.nickname || r.name,
        right: (r) => formatInr(r.balance),
        detail: (r) => [
            r.type,
            r.bank_name,
            r.card_network,
            r.credit_limit != null ? `Limit ${formatInr(r.credit_limit)}` : '',
            r.type === 'card' && r.outstanding != null ? `Outstanding ${formatInr(r.outstanding)}` : '',
            r.statement_day ? `Statement day ${r.statement_day}` : '',
            r.payment_due_day ? `Due day ${r.payment_due_day}` : '',
        ].filter(Boolean),
    },
    bills: {
        label: 'Bills',
        empty: 'No bills found',
        title: (r) => r.name,
        right: (r) => formatInr(r.amount),
        detail: (r) => [
            `Due ${formatDay(r.due_date)}`,
            r.is_recurring ? `Recurs ${r.frequency}` : 'One-off',
            r.is_active ? 'Active' : 'Inactive',
            r.last_paid ? `Last paid ${formatDay(r.last_paid)}` : '',
        ].filter(Boolean),
    },
    subscriptions: {
        label: 'Subscriptions',
        empty: 'No subscriptions found',
        title: (r) => r.name,
        right: (r) => formatInr(r.amount),
        detail: (r) => [
            `Renews ${formatDay(r.due_date)}`,
            r.frequency,
            r.expires_at ? `Expires ${formatDay(r.expires_at)}` : 'Auto-renews',
            r.is_active ? 'Active' : 'Paused',
        ].filter(Boolean),
    },
    splits: {
        label: 'Splits',
        empty: 'No split groups found',
        title: (r) => r.name || 'Untitled group',
        right: (r) => r.inviteStatus || '',
        detail: (r) => [
            r.is_direct ? 'direct' : r.type,
            r.currency,
            r.is_active === false ? 'Archived' : '',
            `Created ${formatDay(r.created_at)}`,
        ].filter(Boolean),
    },
};

/**
 * Read-only paginated list for one of the user's related entities (accounts,
 * bills, subscriptions, splits). Fetches lazily when mounted, i.e. when its
 * drawer tab is first opened, and refetches when the user or page changes.
 */
function UserEntityTab({ kind, userId, getToken }: {
    kind: UserEntityKind;
    userId: string;
    getToken: () => Promise<string | null>;
}) {
    const cfg = USER_ENTITY_CONFIG[kind];
    const [page, setPage] = useState(1);
    const [rows, setRows] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => { setPage(1); }, [userId]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError('');
            try {
                const token = await getToken();
                if (!token) return;
                const res = await fetch(`/api/v1/hq/users/${userId}/${kind}?page=${page}&limit=${DRAWER_PAGE_LIMIT}`, {
                    headers: { 'Authorization': `Bearer ${token}` },
                });
                const json = await res.json();
                if (!res.ok || !json.success) throw new Error(json.error || `Failed to fetch ${kind}`);
                if (cancelled) return;
                setRows(json.data[kind] || []);
                setTotal(json.data.total || 0);
                setTotalPages(json.data.totalPages || 1);
            } catch (err: any) {
                console.error(`HQInsights drawer ${kind} fetch error:`, err);
                if (!cancelled) setError(err.message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [kind, userId, page, getToken]);

    return (
        <div className="flex flex-col flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto">
                <div className="px-6 py-4 text-xs font-bold text-zinc-500 uppercase tracking-wider border-b border-zinc-800 bg-zinc-900/50">
                    {cfg.label}
                    {!loading && <span className="ml-2 text-zinc-600 normal-case font-normal">({total.toLocaleString()} total)</span>}
                </div>

                {error && (
                    <div className="mx-6 mt-4 bg-red-500/10 border border-red-500/50 text-red-400 p-3 rounded-xl text-sm">{error}</div>
                )}

                {loading ? (
                    <div className="divide-y divide-zinc-800">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <div key={i} className="px-6 py-4 flex flex-col gap-3 animate-pulse">
                                <div className="flex justify-between">
                                    <div className="h-3.5 bg-zinc-800 rounded w-32" />
                                    <div className="h-3.5 bg-zinc-800 rounded w-16" />
                                </div>
                                <div className="h-3 bg-zinc-800 rounded w-48" />
                            </div>
                        ))}
                    </div>
                ) : !rows.length ? (
                    <div className="px-6 py-12 text-center text-zinc-500 italic text-sm">{cfg.empty}</div>
                ) : (
                    <div className="divide-y divide-zinc-800">
                        {rows.map((r) => (
                            <div key={r.id} className="px-6 py-4 flex flex-col gap-1.5 hover:bg-zinc-800/40 transition-colors">
                                <div className="flex items-center justify-between gap-3">
                                    <div className="text-sm font-bold text-zinc-200 truncate">{cfg.title(r)}</div>
                                    <div className="text-sm font-semibold text-zinc-300 capitalize shrink-0">{cfg.right(r)}</div>
                                </div>
                                <div className="text-xs text-zinc-500 capitalize">{cfg.detail(r).join(' · ')}</div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {totalPages > 1 && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-zinc-800 bg-zinc-900/80">
                    <button
                        className="px-4 py-2 border border-zinc-800 bg-zinc-950 rounded-xl text-sm font-medium hover:bg-zinc-800 text-zinc-400 transition-colors disabled:opacity-50"
                        disabled={page <= 1 || loading}
                        onClick={() => setPage(p => p - 1)}
                    >
                        Previous
                    </button>
                    <span className="text-sm text-zinc-500">Page {page} of {totalPages}</span>
                    <button
                        className="px-4 py-2 border border-zinc-800 bg-zinc-950 rounded-xl text-sm font-medium hover:bg-zinc-800 text-zinc-300 transition-colors disabled:opacity-50"
                        disabled={page >= totalPages || loading}
                        onClick={() => setPage(p => p + 1)}
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
}

/**
 * HQ admin page showing per-user insights: transaction counts, volumes,
 * last-active timestamps, and a 30-day new-user signup chart.
 *
 * Fetches data from `GET /api/v1/hq/users` (requires HQ admin auth).
 */
export default function HQInsights() {
    const { logout, currentUser, getToken } = useAuth();

    const [data, setData] = useState<InsightsData | null>(null);
    const [page, setPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [sortBy, setSortBy] = useState<'created_at' | 'last_active'>('created_at');
    const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showNumbers, setShowNumbers] = useState(false);

    // Drawer state
    const [selectedUser, setSelectedUser] = useState<UserInsight | null>(null);
    const [drawerData, setDrawerData] = useState<UserTransactionsData | null>(null);
    const [drawerPage, setDrawerPage] = useState(1);
    const [drawerLoading, setDrawerLoading] = useState(false);
    const [drawerError, setDrawerError] = useState('');

    const [drawerTab, setDrawerTab] = useState<'transactions' | 'sms' | UserEntityKind | 'diagnostics'>('transactions');
    const [drawerSmsData, setDrawerSmsData] = useState<UserSmsData | null>(null);
    const [drawerSmsPage, setDrawerSmsPage] = useState(1);
    const [drawerSmsLoading, setDrawerSmsLoading] = useState(false);
    const [drawerSmsError, setDrawerSmsError] = useState('');

    const [isTogglingAi, setIsTogglingAi] = useState<string | null>(null);
    const [isTogglingBlock, setIsTogglingBlock] = useState<string | null>(null);
    // Which on-device-LLM metadata flag is mid-write for the selected user:
    // 'allowlist' | 'debug' | null.
    const [isTogglingLlm, setIsTogglingLlm] = useState<'allowlist' | 'debug' | null>(null);
    const [isTogglingAiChat, setIsTogglingAiChat] = useState(false);
    const [showCandidates, setShowCandidates] = useState(false);

    const { isVisible: isColumnVisible, toggle: toggleColumn, resetToDefault: resetColumns } = useColumnVisibility(COLUMNS_STORAGE_KEY, USER_TABLE_COLUMNS);
    const visibleColumnCount = USER_TABLE_COLUMNS.filter((c) => isColumnVisible(c.key)).length;

    /**
     * Blocks or unblocks a user via `PATCH /api/v1/hq/users/:userId/block`.
     * Blocking prompts for an optional reason (Cancel aborts without calling the API);
     * unblocking asks for a plain confirmation. Updates both the drawer's selected user
     * and the underlying table row in place from the server's response.
     */
    const handleToggleBlock = async (user: UserInsight) => {
        const newStatus = !user.is_blocked;
        let reason: string | null = null;

        if (newStatus) {
            const promptResult = window.prompt(`Block ${getUserDisplayName(user)}? They will immediately lose API access. Optional reason (Cancel to abort):`);
            if (promptResult === null) return;
            reason = promptResult.trim() || null;
        } else if (!window.confirm(`Unblock ${getUserDisplayName(user)}? They will regain full access immediately.`)) {
            return;
        }

        setIsTogglingBlock(user.id);
        try {
            const token = await getToken();
            if (!token) return;

            const res = await fetch(`/api/v1/hq/users/${user.id}/block`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ blocked: newStatus, reason })
            });

            const json = await res.json();
            if (!res.ok || !json.success) throw new Error(json.error || 'Failed to update block status');

            const updates = {
                is_blocked: json.data.is_blocked,
                blocked_at: json.data.blocked_at,
                blocked_reason: json.data.blocked_reason,
                blocked_by: json.data.blocked_by,
            };
            setSelectedUser({ ...user, ...updates });
            if (data) {
                setData({
                    ...data,
                    users: data.users.map(u => u.id === user.id ? { ...u, ...updates } : u)
                });
            }
        } catch (err: any) {
            console.error('Failed to toggle block status:', err);
            alert(err.message || 'Failed to update block status');
        } finally {
            setIsTogglingBlock(null);
        }
    };

    const handleToggleAiFeatures = async (user: UserInsight) => {
        setIsTogglingAi(user.id);
        try {
            const token = await getToken();
            if (!token) return;

            const newStatus = !user.ai_features_enabled;
            const res = await fetch(`/api/v1/hq/users/${user.id}/ai-features`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ enabled: newStatus })
            });

            const json = await res.json();
            if (!res.ok || !json.success) throw new Error(json.error || 'Failed to update AI features');

            // Optimistically reflect the toggle. onDeviceAI is derivable client-side
            // (opt-in AND device capability); onDeviceLLM's rollout maths isn't, so
            // its reason/flag refresh on the next list fetch.
            const patchAiStatus = (u: UserInsight): AiStatus => ({
                ...u.aiStatus,
                aiFeaturesEnabled: newStatus,
                onDeviceAI: newStatus && u.aiStatus.deviceAiCapable,
            });
            setSelectedUser({ ...user, ai_features_enabled: newStatus, aiStatus: patchAiStatus(user) });
            if (data) {
                setData({
                    ...data,
                    users: data.users.map(u => u.id === user.id ? { ...u, ai_features_enabled: newStatus, aiStatus: patchAiStatus(u) } : u)
                });
            }
        } catch (err: any) {
            console.error('Failed to toggle AI features:', err);
            alert(err.message || 'Failed to toggle AI features');
        } finally {
            setIsTogglingAi(null);
        }
    };

    /**
     * Sets a Gemma Tier B override on the selected user via
     * `PATCH /api/v1/hq/users/:userId/on-device-llm` — the allowlist flag
     * (`metadata.on_device_llm_allowlisted`) or the LiteRT-LM debug-screen flag
     * (`metadata.show_litert_lm_debug_page`). Replaces the hand-run Supabase SQL.
     * The server returns the recomputed `aiStatus`; patch both the drawer and the
     * table row from it.
     */
    const handleSetOnDeviceLlm = async (
        user: UserInsight,
        which: 'allowlist' | 'debug',
        value: boolean,
    ) => {
        setIsTogglingLlm(which);
        try {
            const token = await getToken();
            if (!token) return;

            const body = which === 'allowlist' ? { allowlisted: value } : { showDebugPage: value };
            const res = await fetch(`/api/v1/hq/users/${user.id}/on-device-llm`, {
                method: 'PATCH',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });

            const json = await res.json();
            if (!res.ok || !json.success) throw new Error(json.error || 'Failed to update on-device LLM override');

            const updates: Partial<UserInsight> = {
                aiStatus: json.data.aiStatus ?? user.aiStatus,
                metadata: json.data.metadata ?? user.metadata,
            };
            setSelectedUser({ ...user, ...updates });
            if (data) {
                setData({
                    ...data,
                    users: data.users.map(u => (u.id === user.id ? { ...u, ...updates } : u)),
                });
            }
        } catch (err: any) {
            console.error('Failed to update on-device LLM override:', err);
            alert(err.message || 'Failed to update on-device LLM override');
        } finally {
            setIsTogglingLlm(null);
        }
    };

    /**
     * Sets the "Ask Hisab" chat allowlist override on the selected user via
     * `PATCH /api/v1/hq/users/:userId/ai-chat` (`metadata.ai_chat_allowlisted`).
     * The server returns the recomputed `aiStatus`; patch both the drawer and
     * the table row from it.
     */
    const handleSetAiChatAllowlist = async (user: UserInsight, value: boolean) => {
        setIsTogglingAiChat(true);
        try {
            const token = await getToken();
            if (!token) return;

            const res = await fetch(`/api/v1/hq/users/${user.id}/ai-chat`, {
                method: 'PATCH',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ allowlisted: value }),
            });

            const json = await res.json();
            if (!res.ok || !json.success) throw new Error(json.error || 'Failed to update AI chat override');

            const updates: Partial<UserInsight> = {
                aiStatus: json.data.aiStatus ?? user.aiStatus,
                metadata: json.data.metadata ?? user.metadata,
            };
            setSelectedUser({ ...user, ...updates });
            if (data) {
                setData({
                    ...data,
                    users: data.users.map(u => (u.id === user.id ? { ...u, ...updates } : u)),
                });
            }
        } catch (err: any) {
            console.error('Failed to update AI chat override:', err);
            alert(err.message || 'Failed to update AI chat override');
        } finally {
            setIsTogglingAiChat(false);
        }
    };

    /**
     * Fetches a page of SMS logs for the currently selected user and updates drawer state.
     */
    const fetchUserSmsLogs = useCallback(async (user: UserInsight, pageNum: number) => {
        setDrawerSmsLoading(true);
        setDrawerSmsError('');
        try {
            const token = await getToken();
            if (!token) return;

            const res = await fetch(`/api/v1/hq/users/${user.id}/sms?page=${pageNum}&limit=${DRAWER_PAGE_LIMIT}`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });

            const json = await res.json();
            if (!res.ok || !json.success) throw new Error(json.error || 'Failed to fetch SMS logs');

            setDrawerSmsData(json.data);
        } catch (err: any) {
            console.error('HQInsights drawer sms fetch error:', err);
            setDrawerSmsError(err.message);
        } finally {
            setDrawerSmsLoading(false);
        }
    }, [getToken]);

    /**
     * Fetches a page of transactions for the currently selected user and updates drawer state.
     * Resets drawer data when the selected user changes (drawerPage is reset to 1 first).
     *
     * @param user    - The user whose transactions to fetch.
     * @param pageNum - 1-based page number to fetch.
     */
    const fetchUserTransactions = useCallback(async (user: UserInsight, pageNum: number) => {
        setDrawerLoading(true);
        setDrawerError('');
        try {
            const token = await getToken();
            if (!token) return;

            const res = await fetch(`/api/v1/hq/users/${user.id}/transactions?page=${pageNum}&limit=${DRAWER_PAGE_LIMIT}`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });

            const json = await res.json();
            if (!res.ok || !json.success) throw new Error(json.error || 'Failed to fetch transactions');

            setDrawerData(json.data);
        } catch (err: any) {
            console.error('HQInsights drawer fetch error:', err);
            setDrawerError(err.message);
        } finally {
            setDrawerLoading(false);
        }
    }, [getToken]);

    // When the selected user changes, reset to page 1 and fetch fresh.
    useEffect(() => {
        if (!selectedUser) { 
            setDrawerData(null);
            setDrawerSmsData(null);
            setDrawerTab('transactions');
            return; 
        }
        setDrawerPage(1);
        setDrawerSmsPage(1);
        fetchUserTransactions(selectedUser, 1);
        fetchUserSmsLogs(selectedUser, 1);
    }, [selectedUser]); // eslint-disable-line react-hooks/exhaustive-deps

    // When the drawer page changes (but user stays the same), fetch new page.
    useEffect(() => {
        if (!selectedUser || drawerPage === 1) return;
        fetchUserTransactions(selectedUser, drawerPage);
    }, [drawerPage]); // eslint-disable-line react-hooks/exhaustive-deps

    // When the drawer SMS page changes, fetch new SMS page.
    useEffect(() => {
        if (!selectedUser || drawerSmsPage === 1) return;
        fetchUserSmsLogs(selectedUser, drawerSmsPage);
    }, [drawerSmsPage]); // eslint-disable-line react-hooks/exhaustive-deps

    /**
     * Fetches a single page of user insights from the HQ API and updates state.
     * Attaches the current user's Firebase ID token for authentication.
     * On page 1 the response also includes `growthData` for the signup chart.
     *
     * @param pageNum - 1-based page number to fetch.
     * @param searchStr - optional search term to filter results.
     * @param sortField - the field to sort by.
     * @param sortDir - the direction to sort ('asc' or 'desc').
     */
    const fetchInsights = useCallback(async (pageNum: number, searchStr: string, sortField: string, sortDir: string) => {
        setLoading(true);
        setError('');
        try {
            const token = await getToken();
            if (!token) return;

            const res = await fetch(`/api/v1/hq/users?page=${pageNum}&limit=${PAGE_LIMIT}&search=${encodeURIComponent(searchStr)}&sortBy=${sortField}&sortOrder=${sortDir}`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });

            const json = await res.json();
            if (!res.ok || !json.success) throw new Error(json.error || 'Failed to fetch user insights');

            setData({
                ...json.data,
                users: (json.data.users || []).map(normalizeUser),
                rollout: json.data.rollout ?? null,
            });
        } catch (err: any) {
            console.error('HQInsights fetch error:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [getToken]);

    useEffect(() => {
        fetchInsights(page, searchQuery, sortBy, sortOrder);
    }, [fetchInsights, page, searchQuery, sortBy, sortOrder]);

    /**
     * Computes the page number buttons to display in the pagination row.
     * Collapses distant pages into '...' ellipsis entries when there are more than 7 pages,
     * always keeping the first and last page visible.
     *
     * @returns An array of page numbers (as `number`) and ellipsis markers (as `'...'`).
     */
    const pageNumbers = (() => {
        const totalPages = data?.totalPages ?? 1;
        if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
        if (page <= 4) return [1, 2, 3, 4, 5, '...', totalPages];
        if (page >= totalPages - 3) return [1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
        return [1, '...', page - 1, page, page + 1, '...', totalPages];
    })();

    // Derive KPIs from the full dataset summary
    const totalUsers = data?.total ?? 0;
    const activeUsers = data?.growthData?.reduce((sum, d) => sum + d.newUsers, 0) ?? 0;
    const avgTx = totalUsers > 0 && data
        ? (data.users.reduce((s, u) => s + u.txCount, 0) / data.users.length).toFixed(1)
        : '—';

    return (
        <>
        <div className="min-h-screen bg-zinc-950 text-zinc-100 flex">
            {/* Sidebar */}
            <div className="w-64 border-r border-zinc-800 p-6 flex flex-col gap-8">
                <div className="font-bold text-xl text-yellow-400">HQ AppPulse</div>

                <nav className="flex flex-col gap-2 flex-grow">
                    <Link to="/hq/dashboard" className="flex items-center gap-3 px-4 py-3 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/50 rounded-xl transition-colors">
                        Dashboard
                    </Link>
                    <div className="flex items-center gap-3 px-4 py-3 bg-zinc-900 text-yellow-400 rounded-xl font-medium">
                        User Insights
                    </div>
                    <Link to="/hq/transactions" className="flex items-center gap-3 px-4 py-3 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/50 rounded-xl transition-colors">
                        Transactions
                    </Link>
                    <Link to="/hq/notifications" className="flex items-center gap-3 px-4 py-3 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/50 rounded-xl transition-colors">
                        Notifications
                    </Link>
                    <a href="#" className="flex items-center gap-3 px-4 py-3 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/50 rounded-xl transition-colors">
                        Reports
                    </a>
                </nav>

                <div className="flex flex-col gap-4 border-t border-zinc-800 pt-6">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-zinc-800 border border-zinc-700"></div>
                        <div className="overflow-hidden">
                            <div className="text-sm font-bold truncate max-w-[120px]">{currentUser?.email}</div>
                            <div className="text-xs text-zinc-500">HQ Access</div>
                        </div>
                    </div>
                    <button onClick={logout} className="text-sm text-left text-zinc-500 hover:text-red-400 transition-colors px-2">
                        Sign out
                    </button>
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 p-10 overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between gap-4 mb-8">
                    <div>
                        <h1 className="text-3xl font-bold mb-1">User Insights</h1>
                        <p className="text-sm text-zinc-500">Understand how users engage with Hisab Kitab</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => { setSelectedUser(null); setShowCandidates(true); }}
                            className="flex items-center gap-2 bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 px-4 py-2 rounded-xl text-sm font-medium transition-colors text-zinc-300"
                        >
                            <Sparkles className="w-4 h-4 text-yellow-400" />
                            Tier B candidates
                        </button>
                        <ColumnVisibilityMenu
                            columns={USER_TABLE_COLUMNS}
                            isVisible={isColumnVisible}
                            onToggle={toggleColumn}
                            onReset={resetColumns}
                        />
                        <button
                            onClick={() => setShowNumbers(prev => !prev)}
                            className="flex items-center gap-2 bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 px-4 py-2 rounded-xl text-sm font-medium transition-colors text-zinc-300"
                        >
                            {showNumbers ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                            {showNumbers ? 'Hide Numbers' : 'Show Numbers'}
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="bg-red-500/10 border border-red-500/50 text-red-400 p-4 rounded-xl mb-8">
                        {error}
                    </div>
                )}

                {/* On-Device LLM rollout banner */}
                {data?.rollout && (
                    <div
                        className={`mb-8 rounded-xl border px-4 py-3 text-sm flex flex-wrap items-center gap-x-3 gap-y-1 ${
                            data.rollout.onDeviceLlmKillSwitch
                                ? 'bg-red-500/10 border-red-500/40 text-red-300'
                                : 'bg-zinc-900 border-zinc-800 text-zinc-300'
                        }`}
                    >
                        <Sparkles className={`w-4 h-4 flex-shrink-0 ${data.rollout.onDeviceLlmKillSwitch ? 'text-red-400' : 'text-yellow-400'}`} />
                        <span className="font-medium">On-Device LLM rollout</span>
                        <span className="font-bold">
                            {data.rollout.onDeviceLlmKillSwitch
                                ? 'KILL SWITCH ENGAGED — off for everyone'
                                : `${data.rollout.onDeviceLlmRolloutPercent}% of eligible users`}
                        </span>
                        <span className="text-zinc-500">
                            · a user also needs On-Device AI enabled and a capable device
                        </span>
                        <span
                            className="ml-auto inline-flex items-center gap-1 text-[11px] text-zinc-500 cursor-help"
                            title={[
                                'A user is eligible for On-Device LLM (Gemma Tier B) only if, in order:',
                                '1. Kill switch is off (ON_DEVICE_LLM_ENABLED ≠ "false") — overrides everything else.',
                                '2. AI features are opted in (users.ai_features_enabled).',
                                '3. Device meets the RAM/API capability floor (users.device_ai_capable).',
                                '4. Then either: allowlisted (metadata.on_device_llm_allowlisted === true), forcing them in regardless of rollout %,',
                                '   OR their firebase_uid hashes into a bucket (0–99) below the current ON_DEVICE_LLM_ROLLOUT_PERCENT.',
                                '',
                                'The app additionally re-checks device RAM tier and requires Wi-Fi + explicit user consent before downloading the ~2.41 GiB model.',
                            ].join('\n')}
                        >
                            <Info className="w-3.5 h-3.5" />
                            eligibility criteria
                        </span>
                    </div>
                )}

                {/* KPI Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                    {[
                        {
                            label: 'Total Users',
                            value: loading ? '—' : totalUsers.toLocaleString(),
                            icon: <Users className="w-5 h-5 text-yellow-400" />,
                            sub: 'All time registrations',
                        },
                        {
                            label: 'New Users (30d)',
                            value: loading ? '—' : activeUsers.toLocaleString(),
                            icon: <Activity className="w-5 h-5 text-blue-400" />,
                            sub: 'Signed up in last 30 days',
                        },
                        {
                            label: 'Avg Txns / User',
                            value: loading ? '—' : avgTx,
                            icon: <TrendingUp className="w-5 h-5 text-emerald-400" />,
                            sub: 'Across current page',
                        },
                    ].map((kpi, i) => (
                        <div key={i} className="bg-zinc-900 border border-zinc-800 p-6 rounded-2xl">
                            <div className="flex items-center justify-between mb-3">
                                <div className="text-zinc-400 text-sm">{kpi.label}</div>
                                <div className="w-9 h-9 rounded-xl bg-zinc-800 flex items-center justify-center">{kpi.icon}</div>
                            </div>
                            <div className="text-3xl font-bold text-white mb-1">{kpi.value}</div>
                            <div className="text-xs text-zinc-500">{kpi.sub}</div>
                        </div>
                    ))}
                </div>

                {/* New User Signups Chart */}
                <div className="bg-zinc-900 border border-zinc-800 p-6 rounded-2xl mb-10">
                    <h2 className="text-xl font-bold mb-1">New User Signups</h2>
                    <p className="text-zinc-500 text-sm mb-6">Daily registrations over the last 30 days</p>

                    {loading ? (
                        <div className="h-56 flex items-center justify-center text-zinc-500">Loading chart...</div>
                    ) : data?.growthData && data.growthData.length > 0 ? (
                        <div className="h-56 w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={data.growthData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                                    <XAxis
                                        dataKey="date"
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#71717a', fontSize: 12 }}
                                        dy={10}
                                        tickFormatter={(tick, index) =>
                                            index % 7 === 0 || index === data.growthData!.length - 1 ? tick : ''
                                        }
                                    />
                                    <YAxis
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#71717a', fontSize: 12 }}
                                        dx={-10}
                                        allowDecimals={false}
                                    />
                                    <Tooltip
                                        cursor={{ fill: '#27272a' }}
                                        contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: '8px', color: '#f4f4f5' }}
                                        itemStyle={{ color: '#f4f4f5' }}
                                    />
                                    <Bar dataKey="newUsers" name="New Users" fill="#eab308" radius={[4, 4, 0, 0]} maxBarSize={40} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    ) : (
                        <div className="h-56 flex items-center justify-center text-zinc-500 italic">
                            No signup data available
                        </div>
                    )}
                </div>

                {/* Users Table */}
                <div className="bg-zinc-900 rounded-2xl border border-zinc-800 shadow-sm overflow-hidden flex flex-col">
                    <div className="p-4 border-b border-zinc-800 bg-zinc-900/50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="text-sm font-medium text-zinc-300">All Users</div>
                        <div className="flex items-center gap-4">
                            <div className="relative">
                                <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
                                <input
                                    type="text"
                                    placeholder="Search name or email..."
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            setSearchQuery(searchInput);
                                            setPage(1);
                                        }
                                    }}
                                    className="pl-9 pr-8 py-1.5 bg-zinc-950 border border-zinc-800 rounded-lg text-sm text-zinc-100 placeholder-zinc-500 focus:outline-none focus:border-zinc-700 transition-colors w-full sm:w-64"
                                />
                                {searchInput && (
                                    <button
                                        onClick={() => {
                                            if (searchQuery !== '') {
                                                setSearchQuery('');
                                                setPage(1);
                                            }
                                            setSearchInput('');
                                        }}
                                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors"
                                    >
                                        <X className="w-3.5 h-3.5" />
                                    </button>
                                )}
                            </div>
                            <div className="flex gap-2">
                                <select 
                                    className="bg-zinc-800/80 text-sm text-zinc-200 border border-zinc-700/50 rounded-lg px-2 py-1 outline-none focus:border-yellow-400 hover:border-zinc-600 transition-colors"
                                    value={sortBy} 
                                    onChange={(e) => { setSortBy(e.target.value as any); setPage(1); }}
                                >
                                    <option value="created_at">Join Date</option>
                                    <option value="last_active">Last Seen</option>
                                </select>
                                <button 
                                    onClick={() => { setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc'); setPage(1); }}
                                    className="bg-zinc-800/80 text-zinc-200 border border-zinc-700/50 rounded-lg flex items-center justify-center p-1.5 hover:bg-zinc-700 transition-colors"
                                    title={`Sort ${sortOrder === 'desc' ? 'Descending' : 'Ascending'}`}
                                >
                                    {sortOrder === 'desc' ? <ArrowDown className="w-4 h-4" /> : <ArrowUp className="w-4 h-4" />}
                                </button>
                            </div>
                            <div className="text-sm text-zinc-500 whitespace-nowrap hidden md:block border-l border-zinc-800 pl-4 ml-2">
                                {loading ? 'Loading...' : `Showing ${data?.total ? (page - 1) * PAGE_LIMIT + 1 : 0}–${Math.min(page * PAGE_LIMIT, data?.total ?? 0)} of ${(data?.total ?? 0).toLocaleString()} users`}
                            </div>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="border-b border-zinc-800 bg-zinc-950/50">
                                    {isColumnVisible('user') && <th className="px-6 py-4 text-xs font-bold text-zinc-500 uppercase tracking-wider">User</th>}
                                    {isColumnVisible('joined') && <th className="px-6 py-4 text-xs font-bold text-zinc-500 uppercase tracking-wider">Joined</th>}
                                    {isColumnVisible('transactions') && <th className="px-6 py-4 text-xs font-bold text-zinc-500 uppercase tracking-wider text-right">Transactions</th>}
                                    {isColumnVisible('sms') && <th className="px-6 py-4 text-xs font-bold text-zinc-500 uppercase tracking-wider text-right">SMS Logs</th>}
                                    {isColumnVisible('income') && <th className="px-6 py-4 text-xs font-bold text-zinc-500 uppercase tracking-wider text-right">Income</th>}
                                    {isColumnVisible('expense') && <th className="px-6 py-4 text-xs font-bold text-zinc-500 uppercase tracking-wider text-right">Expense</th>}
                                    {isColumnVisible('ai') && <th className="px-6 py-4 text-xs font-bold text-zinc-500 uppercase tracking-wider">AI</th>}
                                    {isColumnVisible('appOta') && <th className="px-6 py-4 text-xs font-bold text-zinc-500 uppercase tracking-wider">App / OTA</th>}
                                    {isColumnVisible('lastSeen') && <th className="px-6 py-4 text-xs font-bold text-zinc-500 uppercase tracking-wider text-right">Last Seen</th>}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-zinc-800">
                                {loading ? (
                                    Array.from({ length: PAGE_LIMIT }).map((_, i) => (
                                        <tr key={i} className="animate-pulse">
                                            {isColumnVisible('user') && <td className="px-6 py-4"><div className="flex items-center gap-3"><div className="w-8 h-8 bg-zinc-800 rounded-full" /><div className="h-4 bg-zinc-800 rounded w-32" /></div></td>}
                                            {isColumnVisible('joined') && <td className="px-6 py-4"><div className="h-4 bg-zinc-800 rounded w-24" /></td>}
                                            {isColumnVisible('transactions') && <td className="px-6 py-4"><div className="h-4 bg-zinc-800 rounded w-12 ml-auto" /></td>}
                                            {isColumnVisible('sms') && <td className="px-6 py-4"><div className="h-4 bg-zinc-800 rounded w-12 ml-auto" /></td>}
                                            {isColumnVisible('income') && <td className="px-6 py-4"><div className="h-4 bg-zinc-800 rounded w-20 ml-auto" /></td>}
                                            {isColumnVisible('expense') && <td className="px-6 py-4"><div className="h-4 bg-zinc-800 rounded w-20 ml-auto" /></td>}
                                            {isColumnVisible('ai') && <td className="px-6 py-4"><div className="h-4 bg-zinc-800 rounded w-16" /></td>}
                                            {isColumnVisible('appOta') && <td className="px-6 py-4"><div className="h-4 bg-zinc-800 rounded w-28" /></td>}
                                            {isColumnVisible('lastSeen') && <td className="px-6 py-4"><div className="h-4 bg-zinc-800 rounded w-16 ml-auto" /></td>}
                                        </tr>
                                    ))
                                ) : !data?.users.length ? (
                                    <tr>
                                        <td colSpan={visibleColumnCount} className="px-6 py-12 text-center text-zinc-500 italic">No users found</td>
                                    </tr>
                                ) : (
                                    data.users.map((user) => (
                                        <tr
                                            key={user.id}
                                            className="hover:bg-zinc-800/50 transition-colors cursor-pointer"
                                            onClick={() => setSelectedUser(user)}
                                        >
                                            {isColumnVisible('user') && (
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-8 h-8 rounded-full bg-yellow-400/10 border border-yellow-400/20 text-yellow-400 flex items-center justify-center font-bold text-xs">
                                                            {getUserInitials(user)}
                                                        </div>
                                                        <div>
                                                            <div className="flex items-center gap-2">
                                                                <div className="text-sm font-medium text-zinc-200">{getUserDisplayName(user)}</div>
                                                                {user.is_blocked && (
                                                                    <span className="flex items-center gap-1 bg-red-500/10 border border-red-500/30 text-red-400 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide">
                                                                        <Ban className="w-2.5 h-2.5" /> Blocked
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {user.display_name && user.email && (
                                                                <div className="text-xs text-zinc-500">{user.email}</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                            )}
                                            {isColumnVisible('joined') && <td className="px-6 py-4 text-sm text-zinc-400">{formatDate(user.created_at)}</td>}
                                            {isColumnVisible('transactions') && <td className="px-6 py-4 text-sm font-bold text-right text-zinc-100">{user.txCount.toLocaleString()}</td>}
                                            {isColumnVisible('sms') && <td className="px-6 py-4 text-sm font-medium text-right text-blue-400">{user.smsStats.total > 0 ? user.smsStats.total.toLocaleString() : '—'}</td>}
                                            {isColumnVisible('income') && <td className="px-6 py-4 text-sm font-medium text-right text-emerald-400">{showNumbers ? formatAmount(user.totalIncome) : '₹ ••••'}</td>}
                                            {isColumnVisible('expense') && <td className="px-6 py-4 text-sm font-medium text-right text-rose-400">{showNumbers ? formatAmount(user.totalExpense) : '₹ ••••'}</td>}
                                            {isColumnVisible('ai') && (
                                                <td className="px-6 py-4">
                                                    <div className="flex flex-col items-start gap-1">
                                                        <AiFlagPill label="AI" on={user.aiStatus.onDeviceAI} title={user.aiStatus.aiFeaturesEnabled ? (user.aiStatus.deviceAiCapable ? 'Enabled' : 'Opt-in on, device not capable') : 'Opt-in off'} />
                                                        <AiFlagPill label="LLM" on={user.aiStatus.onDeviceLLM} title={LLM_REASON_LABELS[user.aiStatus.onDeviceLlmReason]} />
                                                        {!user.aiStatus.onDeviceLLM && (user.onDeviceLlmFit.tier === 'strong' || user.onDeviceLlmFit.tier === 'moderate') && (
                                                            <span
                                                                className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide border ${LLM_FIT_TIER_STYLES[user.onDeviceLlmFit.tier]}`}
                                                                title={`Gemma Tier B fit: ${user.onDeviceLlmFit.tier} (score ${user.onDeviceLlmFit.score})`}
                                                            >
                                                                {user.onDeviceLlmFit.tier} fit
                                                            </span>
                                                        )}
                                                        {user.aiSignals.total > 0 && (
                                                            <span className="text-[10px] text-zinc-500" title={`${user.aiSignals.accepted} accepted · ${user.aiSignals.corrected} corrected`}>
                                                                {user.aiSignals.total} signal{user.aiSignals.total === 1 ? '' : 's'}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                            )}
                                            {isColumnVisible('appOta') && <td className="px-6 py-4 text-sm text-zinc-300 whitespace-nowrap">{formatAppOtaLabel(user)}</td>}
                                            {isColumnVisible('lastSeen') && (
                                                <td className="px-6 py-4 text-sm text-right text-zinc-400">
                                                    <div className="flex items-center justify-end gap-2">
                                                        {formatRelativeDate(user.lastActive)}
                                                        <ChevronRight className="w-4 h-4 text-zinc-600" />
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="p-4 border-t border-zinc-800 flex items-center justify-between bg-zinc-900/50 mt-auto">
                        <button
                            className="px-4 py-2 border border-zinc-800 bg-zinc-950 rounded-xl text-sm font-medium hover:bg-zinc-800 text-zinc-400 transition-colors disabled:opacity-50"
                            disabled={page <= 1 || loading}
                            onClick={() => setPage(p => p - 1)}
                        >
                            Previous
                        </button>
                        <div className="flex items-center gap-1">
                            {pageNumbers.map((p, i) =>
                                p === '...' ? (
                                    <span key={`ellipsis-${i}`} className="px-2 text-zinc-600">...</span>
                                ) : (
                                    <button
                                        key={p}
                                        onClick={() => setPage(p as number)}
                                        className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${page === p ? 'bg-yellow-400 text-zinc-950 font-bold' : 'hover:bg-zinc-800 text-zinc-400'}`}
                                    >
                                        {p}
                                    </button>
                                )
                            )}
                        </div>
                        <button
                            className="px-4 py-2 border border-zinc-800 bg-zinc-950 rounded-xl text-sm font-medium hover:bg-zinc-800 text-zinc-300 transition-colors disabled:opacity-50"
                            disabled={page >= (data?.totalPages ?? 1) || loading}
                            onClick={() => setPage(p => p + 1)}
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>
        </div>

        {/* User transactions drawer */}
        {selectedUser && (
            <>
                {/* Backdrop */}
                <div
                    className="fixed inset-0 bg-zinc-950/60 backdrop-blur-sm z-40"
                    onClick={() => setSelectedUser(null)}
                />

                {/* Panel */}
                <div className="fixed top-0 right-0 h-full w-[480px] bg-zinc-900 border-l border-zinc-800 z-50 flex flex-col shadow-2xl">

                    {/* Drawer header */}
                    <div className="flex items-start justify-between p-6 border-b border-zinc-800">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-full bg-yellow-400/10 border border-yellow-400/20 text-yellow-400 flex items-center justify-center font-bold text-sm">
                                {getUserInitials(selectedUser)}
                            </div>
                            <div>
                                <div className="text-base font-bold text-zinc-100">{getUserDisplayName(selectedUser)}</div>
                                {selectedUser.display_name && selectedUser.email && (
                                    <div className="text-xs text-zinc-500 mt-0.5">{selectedUser.email}</div>
                                )}
                                <div className="text-xs text-zinc-500 mt-0.5">
                                    Joined {formatDate(selectedUser.created_at)} · {formatAppOtaLabel(selectedUser)}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-6 mt-1">
                            <div className="flex items-center gap-2" title="Toggle On-Device AI Features">
                                <span className="text-xs font-medium text-zinc-400">On-Device AI</span>
                                <button
                                    onClick={() => handleToggleAiFeatures(selectedUser)}
                                    disabled={isTogglingAi === selectedUser.id}
                                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${selectedUser.ai_features_enabled ? 'bg-yellow-400' : 'bg-zinc-700'} disabled:opacity-50`}
                                >
                                    <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${selectedUser.ai_features_enabled ? 'translate-x-5' : 'translate-x-1'}`} />
                                </button>
                            </div>
                            <button
                                onClick={() => handleToggleBlock(selectedUser)}
                                disabled={isTogglingBlock === selectedUser.id}
                                title={selectedUser.is_blocked ? 'Unblock this user' : 'Block this user (e.g. bot/abuse)'}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors disabled:opacity-50 ${
                                    selectedUser.is_blocked
                                        ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20'
                                        : 'bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20'
                                }`}
                            >
                                {selectedUser.is_blocked ? <ShieldCheck className="w-3.5 h-3.5" /> : <Ban className="w-3.5 h-3.5" />}
                                {isTogglingBlock === selectedUser.id ? 'Working...' : selectedUser.is_blocked ? 'Unblock' : 'Block'}
                            </button>
                            <button
                                onClick={() => setSelectedUser(null)}
                                className="text-zinc-500 hover:text-zinc-100 transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    {selectedUser.is_blocked && (
                        <div className="mx-6 mt-4 bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-xl text-sm flex items-start gap-2">
                            <Ban className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            <div>
                                <div className="font-bold">Account blocked</div>
                                <div className="text-red-300/90 mt-0.5">
                                    {selectedUser.blocked_reason || 'No reason given'}
                                    {selectedUser.blocked_by && ` — by ${selectedUser.blocked_by}`}
                                    {selectedUser.blocked_at && ` on ${formatDateTime(selectedUser.blocked_at)}`}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="flex px-6 items-center gap-6 border-b border-zinc-800 bg-zinc-950/50 overflow-x-auto whitespace-nowrap">
                        <button 
                            onClick={() => setDrawerTab('transactions')}
                            className={`py-4 text-sm font-medium border-b-2 transition-colors ${drawerTab === 'transactions' ? 'border-yellow-400 text-yellow-400' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}
                        >
                            Transactions
                        </button>
                        <button 
                            onClick={() => setDrawerTab('sms')}
                            className={`py-4 text-sm font-medium border-b-2 transition-colors ${drawerTab === 'sms' ? 'border-yellow-400 text-yellow-400' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}
                        >
                            SMS Logs
                        </button>
                        {(Object.keys(USER_ENTITY_CONFIG) as UserEntityKind[]).map((kind) => (
                            <button
                                key={kind}
                                onClick={() => setDrawerTab(kind)}
                                className={`py-4 text-sm font-medium border-b-2 transition-colors ${drawerTab === kind ? 'border-yellow-400 text-yellow-400' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}
                            >
                                {USER_ENTITY_CONFIG[kind].label}
                            </button>
                        ))}
                        <button
                            onClick={() => setDrawerTab('diagnostics')}
                            className={`py-4 text-sm font-medium border-b-2 transition-colors ${drawerTab === 'diagnostics' ? 'border-yellow-400 text-yellow-400' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}
                        >
                            Diagnostics
                        </button>
                    </div>

                    {drawerTab === 'transactions' && (
                        <div className="flex flex-col flex-1 overflow-hidden">
                            {/* User stat pills */}
                            <div className="flex items-center gap-3 px-6 py-4 border-b border-zinc-800">
                                <div className="flex-1 bg-zinc-800/60 rounded-xl px-4 py-3 text-center">
                                    <div className="text-lg font-bold text-zinc-100">{selectedUser.txCount.toLocaleString()}</div>
                                    <div className="text-xs text-zinc-500 mt-0.5">Transactions</div>
                                </div>
                                <div className="flex-1 bg-emerald-500/10 border border-emerald-500/20 rounded-xl px-4 py-3 text-center">
                                    <div className="text-lg font-bold text-emerald-400">{showNumbers ? formatAmount(selectedUser.totalIncome) : '₹ ••••'}</div>
                                    <div className="text-xs text-zinc-500 mt-0.5">Total Income</div>
                                </div>
                                <div className="flex-1 bg-rose-500/10 border border-rose-500/20 rounded-xl px-4 py-3 text-center">
                                    <div className="text-lg font-bold text-rose-400">{showNumbers ? formatAmount(selectedUser.totalExpense) : '₹ ••••'}</div>
                                    <div className="text-xs text-zinc-500 mt-0.5">Total Expense</div>
                                </div>
                            </div>

                            {/* Transaction list */}
                            <div className="flex-1 overflow-y-auto">
                                <div className="px-6 py-4 text-xs font-bold text-zinc-500 uppercase tracking-wider border-b border-zinc-800">
                                    Transactions
                                    {drawerData && (
                                        <span className="ml-2 text-zinc-600 normal-case font-normal">
                                            ({drawerData.total.toLocaleString()} total)
                                        </span>
                                    )}
                                </div>

                                {drawerError && (
                                    <div className="mx-6 mt-4 bg-red-500/10 border border-red-500/50 text-red-400 p-3 rounded-xl text-sm">
                                        {drawerError}
                                    </div>
                                )}

                                {drawerLoading ? (
                                    <div className="divide-y divide-zinc-800">
                                        {Array.from({ length: 8 }).map((_, i) => (
                                            <div key={i} className="px-6 py-4 flex items-center justify-between animate-pulse">
                                                <div className="flex flex-col gap-2">
                                                    <div className="h-3.5 bg-zinc-800 rounded w-36" />
                                                    <div className="h-3 bg-zinc-800 rounded w-24" />
                                                </div>
                                                <div className="h-4 bg-zinc-800 rounded w-20" />
                                            </div>
                                        ))}
                                    </div>
                                ) : !drawerData?.transactions.length ? (
                                    <div className="px-6 py-12 text-center text-zinc-500 italic text-sm">
                                        No transactions found
                                    </div>
                                ) : (
                                    <div className="divide-y divide-zinc-800">
                                        {drawerData.transactions.map((tx) => {
                                            const isIncome = tx.type.toLowerCase() === 'income';
                                            return (
                                                <div key={tx.id} className="px-6 py-4 flex items-center justify-between hover:bg-zinc-800/40 transition-colors">
                                                    <div className="flex items-center gap-3 min-w-0">
                                                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${isIncome ? 'bg-emerald-400' : 'bg-rose-400'}`} />
                                                        <div className="min-w-0">
                                                            <div className="text-sm text-zinc-200 truncate">
                                                                {tx.description || <span className="italic text-zinc-500">No description</span>}
                                                            </div>
                                                            <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-2">
                                                                {formatDateTime(tx.created_at)}
                                                                <span className="bg-zinc-800 text-zinc-400 px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide">
                                                                    {tx.source === 'sms' ? 'AUTO' : 'MANUAL'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className={`text-sm font-bold ml-4 flex-shrink-0 ${isIncome ? 'text-emerald-400' : 'text-rose-400'}`}>
                                                        {showNumbers ? `${isIncome ? '+' : '−'}${formatAmount(tx.amount)}` : `${isIncome ? '+' : '−'}₹ ••••`}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {/* Drawer pagination */}
                            {drawerData && drawerData.totalPages > 1 && (
                                <div className="flex items-center justify-between px-6 py-4 border-t border-zinc-800 bg-zinc-900/80">
                                    <button
                                        className="px-4 py-2 border border-zinc-800 bg-zinc-950 rounded-xl text-sm font-medium hover:bg-zinc-800 text-zinc-400 transition-colors disabled:opacity-50"
                                        disabled={drawerPage <= 1 || drawerLoading}
                                        onClick={() => setDrawerPage(p => p - 1)}
                                    >
                                        Previous
                                    </button>
                                    <span className="text-sm text-zinc-500">
                                        Page {drawerPage} of {drawerData.totalPages}
                                    </span>
                                    <button
                                        className="px-4 py-2 border border-zinc-800 bg-zinc-950 rounded-xl text-sm font-medium hover:bg-zinc-800 text-zinc-300 transition-colors disabled:opacity-50"
                                        disabled={drawerPage >= drawerData.totalPages || drawerLoading}
                                        onClick={() => setDrawerPage(p => p + 1)}
                                    >
                                        Next
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {drawerTab === 'sms' && (
                        <div className="flex flex-col flex-1 overflow-hidden">
                            {/* SMS Stat Pills */}
                            <div className="flex items-center gap-3 px-6 py-4 border-b border-zinc-800">
                                <div className="flex-1 bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-3 text-center">
                                    <div className="text-lg font-bold text-amber-400">{selectedUser.smsStats.pending.toLocaleString()}</div>
                                    <div className="text-xs text-zinc-500 mt-0.5">Pending</div>
                                </div>
                                <div className="flex-1 bg-emerald-500/10 border border-emerald-500/20 rounded-xl px-4 py-3 text-center">
                                    <div className="text-lg font-bold text-emerald-400">{selectedUser.smsStats.processed.toLocaleString()}</div>
                                    <div className="text-xs text-zinc-500 mt-0.5">Processed</div>
                                </div>
                                <div className="flex-1 bg-zinc-800/60 rounded-xl px-4 py-3 text-center">
                                    <div className="text-lg font-bold text-zinc-100">{selectedUser.smsStats.ignored.toLocaleString()}</div>
                                    <div className="text-xs text-zinc-500 mt-0.5">Ignored</div>
                                </div>
                            </div>

                            {/* SMS list */}
                            <div className="flex-1 overflow-y-auto">
                                <div className="px-6 py-4 text-xs font-bold text-zinc-500 uppercase tracking-wider border-b border-zinc-800 flex justify-between items-center bg-zinc-900/50">
                                    <span>
                                        Scanned SMS
                                        {drawerSmsData && (
                                            <span className="ml-2 text-zinc-600 normal-case font-normal">
                                                ({drawerSmsData.total.toLocaleString()} total)
                                            </span>
                                        )}
                                    </span>
                                </div>

                                {drawerSmsError && (
                                    <div className="mx-6 mt-4 bg-red-500/10 border border-red-500/50 text-red-400 p-3 rounded-xl text-sm">
                                        {drawerSmsError}
                                    </div>
                                )}

                                {drawerSmsLoading ? (
                                    <div className="divide-y divide-zinc-800">
                                        {Array.from({ length: 8 }).map((_, i) => (
                                            <div key={i} className="px-6 py-4 flex flex-col gap-3 animate-pulse">
                                                <div className="flex justify-between">
                                                    <div className="h-3.5 bg-zinc-800 rounded w-24" />
                                                    <div className="h-3.5 bg-zinc-800 rounded w-16" />
                                                </div>
                                                <div className="h-3 bg-zinc-800 rounded w-full" />
                                            </div>
                                        ))}
                                    </div>
                                ) : !drawerSmsData?.smsLogs.length ? (
                                    <div className="px-6 py-12 text-center text-zinc-500 italic text-sm">
                                        No SMS logs found
                                    </div>
                                ) : (
                                    <div className="divide-y divide-zinc-800">
                                        {drawerSmsData.smsLogs.map((sms) => (
                                            <div key={sms.id} className="px-6 py-4 flex flex-col gap-2 hover:bg-zinc-800/40 transition-colors">
                                                <div className="flex items-center justify-between">
                                                    <div className="text-sm font-bold text-zinc-200">{sms.sender}</div>
                                                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest ${
                                                        sms.status === 'pending' ? 'bg-amber-400/10 text-amber-400 border border-amber-400/20' :
                                                        sms.status === 'processed' ? 'bg-emerald-400/10 text-emerald-400 border border-emerald-400/20' :
                                                        'bg-zinc-800 text-zinc-400 border border-zinc-700'
                                                    }`}>
                                                        {sms.status}
                                                    </span>
                                                </div>
                                                <p className="text-xs text-zinc-400 leading-relaxed break-words line-clamp-3">
                                                    {sms.rawSMS}
                                                </p>
                                                <div className="flex items-center justify-between mt-1">
                                                    <div className="text-[10px] text-zinc-600 font-medium">
                                                        {formatDateTime(sms.sms_date || sms.created_at)}
                                                    </div>
                                                    {!sms.isTransaction && (
                                                        <span className="text-[10px] text-zinc-500 italic">Non-transactional</span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Drawer SMS pagination */}
                            {drawerSmsData && drawerSmsData.totalPages > 1 && (
                                <div className="flex items-center justify-between px-6 py-4 border-t border-zinc-800 bg-zinc-900/80">
                                    <button
                                        className="px-4 py-2 border border-zinc-800 bg-zinc-950 rounded-xl text-sm font-medium hover:bg-zinc-800 text-zinc-400 transition-colors disabled:opacity-50"
                                        disabled={drawerSmsPage <= 1 || drawerSmsLoading}
                                        onClick={() => setDrawerSmsPage(p => p - 1)}
                                    >
                                        Previous
                                    </button>
                                    <span className="text-sm text-zinc-500">
                                        Page {drawerSmsPage} of {drawerSmsData.totalPages}
                                    </span>
                                    <button
                                        className="px-4 py-2 border border-zinc-800 bg-zinc-950 rounded-xl text-sm font-medium hover:bg-zinc-800 text-zinc-300 transition-colors disabled:opacity-50"
                                        disabled={drawerSmsPage >= drawerSmsData.totalPages || drawerSmsLoading}
                                        onClick={() => setDrawerSmsPage(p => p + 1)}
                                    >
                                        Next
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {drawerTab in USER_ENTITY_CONFIG && (
                        <React.Fragment key={`${selectedUser.id}-${drawerTab}`}>
                            <UserEntityTab
                                kind={drawerTab as UserEntityKind}
                                userId={selectedUser.id}
                                getToken={getToken}
                            />
                        </React.Fragment>
                    )}

                    {drawerTab === 'diagnostics' && (
                        <div className="flex flex-col flex-1 overflow-y-auto p-6 gap-6">
                            {/* AI Features */}
                            <div className="bg-zinc-905/20 border border-zinc-800 rounded-xl p-4 flex flex-col gap-4">
                                <h3 className="text-xs font-bold text-zinc-500 uppercase tracking-wider">AI Features</h3>

                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <div className="text-zinc-500 text-xs">On-Device AI</div>
                                        <div className={`mt-0.5 font-medium ${selectedUser.aiStatus.onDeviceAI ? 'text-yellow-400' : 'text-zinc-400'}`}>
                                            {selectedUser.aiStatus.onDeviceAI ? 'Enabled' : 'Disabled'}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-zinc-500 text-xs">On-Device LLM</div>
                                        <div className={`mt-0.5 font-medium ${selectedUser.aiStatus.onDeviceLLM ? 'text-yellow-400' : 'text-zinc-400'}`}>
                                            {selectedUser.aiStatus.onDeviceLLM ? 'Enabled' : 'Disabled'}
                                            <span className="text-zinc-500 font-normal"> · {LLM_REASON_LABELS[selectedUser.aiStatus.onDeviceLlmReason]}</span>
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-zinc-500 text-xs">AI opt-in (ai_features_enabled)</div>
                                        <div className="text-zinc-200 mt-0.5">{selectedUser.aiStatus.aiFeaturesEnabled ? 'Yes' : 'No'}</div>
                                    </div>
                                    <div>
                                        <div className="text-zinc-500 text-xs">Device capable</div>
                                        <div className="text-zinc-200 mt-0.5">{selectedUser.aiStatus.deviceAiCapable ? 'Yes' : 'No'}</div>
                                    </div>
                                    <div>
                                        <div className="text-zinc-500 text-xs">Rollout bucket</div>
                                        <div className="text-zinc-200 mt-0.5">
                                            {selectedUser.aiStatus.onDeviceLlmBucket === null
                                                ? '—'
                                                : `${selectedUser.aiStatus.onDeviceLlmBucket} / ${selectedUser.aiStatus.onDeviceLlmRolloutPercent}% ${selectedUser.aiStatus.onDeviceLlmBucket < selectedUser.aiStatus.onDeviceLlmRolloutPercent ? '(in)' : '(out)'}`}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-zinc-500 text-xs">Allowlisted</div>
                                        <div className="text-zinc-200 mt-0.5">{selectedUser.aiStatus.onDeviceLlmAllowlisted ? 'Yes' : 'No'}</div>
                                    </div>
                                    <div>
                                        <div className="text-zinc-500 text-xs">Ask Hisab chat</div>
                                        <div className={`mt-0.5 font-medium ${selectedUser.aiStatus.aiChat ? 'text-yellow-400' : 'text-zinc-400'}`}>
                                            {selectedUser.aiStatus.aiChat ? 'Enabled' : 'Disabled'}
                                            <span className="text-zinc-500 font-normal"> · {AI_CHAT_REASON_LABELS[selectedUser.aiStatus.aiChatReason]}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Ask Hisab chat controls */}
                                <div className="border-t border-zinc-800 pt-4 flex flex-col gap-3">
                                    <div className="text-zinc-500 text-xs font-bold uppercase tracking-wider">Ask Hisab chat controls</div>

                                    <label className="flex items-center justify-between gap-3">
                                        <span className="text-sm text-zinc-300">
                                            Allowlist for Ask Hisab chat
                                            <span className="block text-[11px] text-zinc-500">metadata.ai_chat_allowlisted · needs On-Device LLM too</span>
                                        </span>
                                        <button
                                            onClick={() => handleSetAiChatAllowlist(selectedUser, !selectedUser.aiStatus.aiChatAllowlisted)}
                                            disabled={isTogglingAiChat}
                                            className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${selectedUser.aiStatus.aiChatAllowlisted ? 'bg-yellow-400' : 'bg-zinc-700'}`}
                                        >
                                            <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${selectedUser.aiStatus.aiChatAllowlisted ? 'translate-x-5' : 'translate-x-1'}`} />
                                        </button>
                                    </label>

                                    {isTogglingAiChat && <div className="text-[11px] text-zinc-500 italic">Saving…</div>}
                                </div>

                                {/* Gemma Tier B controls — replace the hand-run Supabase SQL */}
                                <div className="border-t border-zinc-800 pt-4 flex flex-col gap-3">
                                    <div className="text-zinc-500 text-xs font-bold uppercase tracking-wider">Gemma Tier B controls</div>

                                    <label className="flex items-center justify-between gap-3">
                                        <span className="text-sm text-zinc-300">
                                            Allowlist for Tier B
                                            <span className="block text-[11px] text-zinc-500">metadata.on_device_llm_allowlisted · needs AI opt-in too</span>
                                        </span>
                                        <button
                                            onClick={() => handleSetOnDeviceLlm(selectedUser, 'allowlist', !selectedUser.aiStatus.onDeviceLlmAllowlisted)}
                                            disabled={isTogglingLlm !== null}
                                            className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${selectedUser.aiStatus.onDeviceLlmAllowlisted ? 'bg-yellow-400' : 'bg-zinc-700'}`}
                                        >
                                            <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${selectedUser.aiStatus.onDeviceLlmAllowlisted ? 'translate-x-5' : 'translate-x-1'}`} />
                                        </button>
                                    </label>

                                    <label className="flex items-center justify-between gap-3">
                                        <span className="text-sm text-zinc-300">
                                            LiteRT-LM debug screen
                                            <span className="block text-[11px] text-zinc-500">metadata.show_litert_lm_debug_page</span>
                                        </span>
                                        <button
                                            onClick={() => handleSetOnDeviceLlm(selectedUser, 'debug', !(selectedUser.metadata?.show_litert_lm_debug_page === true))}
                                            disabled={isTogglingLlm !== null}
                                            className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${selectedUser.metadata?.show_litert_lm_debug_page === true ? 'bg-yellow-400' : 'bg-zinc-700'}`}
                                        >
                                            <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${selectedUser.metadata?.show_litert_lm_debug_page === true ? 'translate-x-5' : 'translate-x-1'}`} />
                                        </button>
                                    </label>

                                    {isTogglingLlm && <div className="text-[11px] text-zinc-500 italic">Saving…</div>}

                                    {/* Fit assessment */}
                                    <div className="mt-1 flex items-center gap-2">
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide border ${LLM_FIT_TIER_STYLES[selectedUser.onDeviceLlmFit.tier]}`}>
                                            {selectedUser.onDeviceLlmFit.tier} fit
                                        </span>
                                        <span className="text-[11px] text-zinc-500">score {selectedUser.onDeviceLlmFit.score}</span>
                                    </div>
                                    {selectedUser.onDeviceLlmFit.blockers.length > 0 && (
                                        <div className="text-[11px] text-rose-400/90">
                                            Blockers: {selectedUser.onDeviceLlmFit.blockers.map(b => LLM_FIT_BLOCKER_LABELS[b] || b).join(', ')}
                                        </div>
                                    )}
                                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-zinc-500">
                                        <div>Rule-based merchant signals: <span className="text-zinc-300">{selectedUser.onDeviceLlmFit.demand.ruleBasedMerchantSignals}</span></div>
                                        <div>Corrected: <span className="text-zinc-300">{selectedUser.onDeviceLlmFit.demand.ruleBasedCorrectionRate === null ? '—' : `${Math.round(selectedUser.onDeviceLlmFit.demand.ruleBasedCorrectionRate * 100)}%`}</span></div>
                                        <div>SMS transactions: <span className="text-zinc-300">{selectedUser.onDeviceLlmFit.demand.smsTransactions}</span></div>
                                        <div>Active (30d): <span className="text-zinc-300">{selectedUser.onDeviceLlmFit.engagement.activeLast30d ? 'yes' : 'no'}</span></div>
                                    </div>
                                </div>

                                {/* AI correction signals */}
                                <div className="border-t border-zinc-800 pt-4">
                                    <div className="flex items-baseline justify-between">
                                        <div className="text-zinc-500 text-xs font-bold uppercase tracking-wider">AI Signals</div>
                                        <div className="text-xs text-zinc-400">
                                            {selectedUser.aiSignals.total.toLocaleString()} total ·{' '}
                                            <span className="text-emerald-400">{selectedUser.aiSignals.accepted.toLocaleString()} accepted</span> ·{' '}
                                            <span className="text-rose-400">{selectedUser.aiSignals.corrected.toLocaleString()} corrected</span>
                                        </div>
                                    </div>

                                    {selectedUser.aiSignals.total === 0 ? (
                                        <div className="text-xs text-zinc-600 italic mt-2">No AI suggestion signals recorded</div>
                                    ) : (
                                        <div className="mt-3 flex flex-col gap-4">
                                            {([
                                                ['By feature', selectedUser.aiSignals.byFeature],
                                                ['By model', selectedUser.aiSignals.byModel],
                                            ] as [string, Record<string, AiSignalGroup>][]).map(([heading, groups]) => (
                                                <div key={heading}>
                                                    <div className="text-[11px] text-zinc-500 mb-1">{heading}</div>
                                                    <div className="flex flex-col gap-1">
                                                        {Object.entries(groups).length === 0 ? (
                                                            <div className="text-[11px] text-zinc-600 italic">—</div>
                                                        ) : (
                                                            Object.entries(groups).map(([key, g]) => {
                                                                const acceptPct = g.total > 0 ? Math.round((g.accepted / g.total) * 100) : 0;
                                                                return (
                                                                    <div key={key} className="flex items-center justify-between text-xs bg-zinc-950/60 border border-zinc-800 rounded-lg px-3 py-1.5">
                                                                        <span className="font-mono text-zinc-300">{key}</span>
                                                                        <span className="text-zinc-500">
                                                                            <span className="text-emerald-400">{g.accepted}</span>
                                                                            {' / '}
                                                                            <span className="text-rose-400">{g.corrected}</span>
                                                                            {' '}<span className="text-zinc-600">({acceptPct}% kept, n={g.total})</span>
                                                                        </span>
                                                                    </div>
                                                                );
                                                            })
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Device & Hardware Specs */}
                            <div className="bg-zinc-905/20 border border-zinc-800 rounded-xl p-4 flex flex-col gap-4">
                                <h3 className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Device & Hardware Specs</h3>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <div className="text-zinc-500 text-xs">Device Model</div>
                                        <div className="text-zinc-200 mt-0.5">{selectedUser.device_model || '—'}</div>
                                    </div>
                                    <div>
                                        <div className="text-zinc-500 text-xs">AI Capable</div>
                                        <div className="text-zinc-200 mt-0.5">
                                            {selectedUser.device_ai_capable === null 
                                                ? '—' 
                                                : selectedUser.device_ai_capable ? 'Yes' : 'No'}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-zinc-500 text-xs">Device RAM</div>
                                        <div className="text-zinc-200 mt-0.5">
                                            {selectedUser.device_ram_mb 
                                                ? `${(selectedUser.device_ram_mb / 1024).toFixed(1)} GB (${selectedUser.device_ram_mb} MB)` 
                                                : '—'}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-zinc-500 text-xs">Android API Level</div>
                                        <div className="text-zinc-200 mt-0.5">{selectedUser.device_api_level || '—'}</div>
                                    </div>
                                    <div className="col-span-2">
                                        <div className="text-zinc-500 text-xs">Specs Last Checked</div>
                                        <div className="text-zinc-200 mt-0.5">
                                            {selectedUser.device_last_checked 
                                                ? formatDateTime(selectedUser.device_last_checked) 
                                                : '—'}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* App Version & OTA Updates */}
                            <div className="bg-zinc-905/20 border border-zinc-800 rounded-xl p-4 flex flex-col gap-4">
                                <h3 className="text-xs font-bold text-zinc-500 uppercase tracking-wider">App Version & Updates</h3>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <div className="text-zinc-500 text-xs">App Version</div>
                                        <div className="text-zinc-200 mt-0.5">{selectedUser.last_seen_app_version ? `v${selectedUser.last_seen_app_version}` : '—'}</div>
                                    </div>
                                    <div>
                                        <div className="text-zinc-500 text-xs">Runtime Version</div>
                                        <div className="text-zinc-200 mt-0.5">{selectedUser.last_seen_runtime_version || '—'}</div>
                                    </div>
                                    <div className="col-span-2">
                                        <div className="text-zinc-500 text-xs">Update ID (OTA)</div>
                                        <div className="text-zinc-200 mt-0.5 font-mono text-xs break-all select-all bg-zinc-950 p-2 border border-zinc-800 rounded-lg">
                                            {selectedUser.last_seen_ota_update_id || '—'}
                                        </div>
                                    </div>
                                    <div className="col-span-2">
                                        <div className="text-zinc-500 text-xs">Update Released At</div>
                                        <div className="text-zinc-200 mt-0.5">
                                            {selectedUser.last_seen_ota_created_at 
                                                ? formatDateTime(selectedUser.last_seen_ota_created_at) 
                                                : '—'}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Session Activity */}
                            <div className="bg-zinc-905/20 border border-zinc-800 rounded-xl p-4 flex flex-col gap-4">
                                <h3 className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Activity Heartbeat</h3>
                                <div className="text-sm">
                                    <div className="text-zinc-500 text-xs">Last Seen At</div>
                                    <div className="text-zinc-200 mt-0.5">
                                        {selectedUser.last_seen_at ? formatDateTime(selectedUser.last_seen_at) : '—'}
                                    </div>
                                </div>
                            </div>

                            {/* Profile Metadata */}
                            <div className="bg-zinc-905/20 border border-zinc-800 rounded-xl p-4 flex flex-col gap-4">
                                <h3 className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Profile Metadata</h3>
                                <div className="mt-1">
                                    <pre className="bg-zinc-950 p-4 rounded-xl border border-zinc-800 text-[11px] font-mono text-zinc-300 overflow-x-auto max-h-60 overflow-y-auto">
                                        {JSON.stringify(selectedUser.metadata || {}, null, 2)}
                                    </pre>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </>
        )}

        {showCandidates && (
            <TierBCandidatesModal
                getToken={getToken}
                onClose={() => setShowCandidates(false)}
                onChanged={() => fetchInsights(page, searchQuery, sortBy, sortOrder)}
            />
        )}
        </>
    );
}
